/**
 * La coupure de session sur inactivité, vue du navigateur (suivora-api#887).
 *
 * **Ce que le serveur mesure.** Le jeton de rafraîchissement tourne à chaque renouvellement, et
 * l'API compare la date de création du jeton courant au réglage de l'organisation. L'inactivité
 * mesurée est donc « le temps écoulé depuis le dernier renouvellement », pas « le temps écoulé
 * depuis le dernier geste de l'utilisateur ».
 *
 * **Pourquoi cet écart coûte.** Le web ne renouvelait qu'en réaction à un 401. Quelqu'un qui
 * remplit un CRA pendant quarante minutes ne déclenche aucune requête : il est parfaitement actif
 * et compté comme inactif. Sa session tombe à l'enregistrement, et la saisie part avec.
 *
 * D'où les deux moitiés, et l'ordre entre elles : **faire coïncider l'inactivité mesurée avec
 * l'inactivité réelle** en renouvelant tant que l'utilisateur agit vraiment ; et **prévenir** celui
 * qui s'est absenté, avant que la session ne se ferme.
 *
 * Le serveur ne peut faire ni l'un ni l'autre : il ne revoit la session qu'au renouvellement, et
 * n'a aucun moyen d'atteindre un onglet ouvert.
 *
 * ## Les deux plafonds
 *
 * `sessionMaxHours` borne la durée **totale** depuis l'ouverture, et les deux se cumulent : la
 * première limite atteinte l'emporte. Le temps réellement restant est donc le **minimum** des deux
 * échéances. N'en décompter qu'une pouvait annoncer vingt-huit minutes quand il en restait deux —
 * et un décompte généreux est pire que pas de décompte, la coupure tombant avant l'annonce.
 *
 * La fin absolue vit dans un cookie httpOnly, illisible du navigateur. Elle arrive donc par la
 * réponse de renouvellement (suivora-api#927), qui la porte déjà résolue et se rafraîchit d'
 * elle-même à chaque rotation.
 *
 * **Les deux motifs n'appellent pas le même geste**, et c'est ce qui justifie de les distinguer
 * jusque dans le type : « pour inactivité » se repousse d'un clic ; « durée maximale atteinte »
 * ne se repousse pas — elle demande d'enregistrer son travail, et la fenêtre ne doit donc pas
 * empêcher de le faire.
 */

/** Renouvellement au plus tous les N — sous le plus court réglage qu'on rencontre. */
const DEFAULT_ROTATE_EVERY_MS = 4 * 60_000;

/** Jamais plus souvent : un renouvellement révoque le jeton précédent, ce n'est pas gratuit. */
const MIN_ROTATE_EVERY_MS = 60_000;

/** Le préavis maximal. Au-delà, l'avertissement arrive quand on n'y pense plus. */
const MAX_WARN_BEFORE_MS = 2 * 60_000;

/**
 * Le réglage de l'organisation, tel que le client peut le connaître.
 *
 * Les trois valeurs disent trois choses différentes, et les confondre se paie. Ce sont celles du
 * contrat, servies sur `GET /auth/me` depuis suivora-api#920 :
 * - `undefined` — **on ne sait pas** : une API antérieure au champ. On entretient la session, on
 *   ne prétend pas connaître d'échéance.
 * - `null` ou `0` — **aucune coupure** n'est configurée. Rien à entretenir, rien à annoncer.
 * - un nombre — la coupure a lieu après ce nombre de minutes sans renouvellement.
 */
export type IdleTimeoutSetting = number | null | undefined;

/**
 * Laquelle des deux limites tombe la première.
 *
 * `idle` se repousse, `absolute` non. Confondre les deux ferait proposer « Rester connecté » sur
 * une échéance que rien ne peut repousser — un bouton qui ne tient pas sa promesse, au moment où
 * l'on avait besoin d'aller enregistrer.
 */
export type SessionLimit = 'idle' | 'absolute';

export type IdlePhase =
  | { kind: 'safe' }
  | { kind: 'warn'; msLeft: number; limit: SessionLimit; deadline: number }
  | { kind: 'expired'; limit: SessionLimit };

/** Y a-t-il une coupure à anticiper ? `null`, `0` et les valeurs absurdes valent « non ». */
export function idleTimeoutMs(setting: IdleTimeoutSetting): number | null {
  if (setting == null || !Number.isFinite(setting) || setting <= 0) return null;
  return setting * 60_000;
}

/**
 * Tous les combien renouveler tant que l'utilisateur agit.
 *
 * Un tiers du délai : deux renouvellements manqués ne suffisent pas à couper. Le calcul reste
 * borné des deux côtés — un réglage à deux minutes ne doit pas déclencher une requête toutes les
 * quarante secondes, et un réglage à huit heures ne doit pas laisser passer huit heures.
 */
export function rotateEveryMs(setting: IdleTimeoutSetting): number {
  const timeout = idleTimeoutMs(setting);
  if (timeout === null) return DEFAULT_ROTATE_EVERY_MS;
  return Math.max(MIN_ROTATE_EVERY_MS, Math.min(DEFAULT_ROTATE_EVERY_MS, timeout / 3));
}

/** Combien de temps avant la coupure l'avertissement s'affiche. */
export function warnBeforeMs(setting: IdleTimeoutSetting): number {
  const timeout = idleTimeoutMs(setting);
  if (timeout === null) return MAX_WARN_BEFORE_MS;
  return Math.min(MAX_WARN_BEFORE_MS, timeout / 3);
}

/**
 * La fin absolue, telle que la réponse de renouvellement la sert.
 *
 * `null` — aucun plafond ; **absent** — API antérieure au champ ; une date invalide est traitée
 * comme absente plutôt que comme zéro, sans quoi `Date.parse` rendrait `NaN` et toute comparaison
 * serait fausse en silence.
 */
export function absoluteDeadline(sessionExpiresAt: string | null | undefined): number | null {
  if (!sessionExpiresAt) return null;
  const at = Date.parse(sessionExpiresAt);
  return Number.isFinite(at) ? at : null;
}

/**
 * Où en est la session.
 *
 * `rotatedAt` est le dernier renouvellement **que cet onglet a fait lui-même**. Tant qu'il vaut
 * `null`, l'échéance d'inactivité est inconnue : la session pouvait être ouverte depuis une heure
 * quand la page s'est chargée, et le jeton dater d'autant. On ne décompte donc pas cet axe — un
 * compte à rebours faux dans le sens généreux serait le pire des deux, puisque la coupure tomberait
 * avant l'annonce.
 *
 * La fin absolue, elle, ne dépend d'aucune ancre : c'est une date. Elle vaut dès qu'on la connaît.
 */
export function idlePhase(args: {
  setting: IdleTimeoutSetting;
  rotatedAt: number | null;
  sessionExpiresAt?: string | null;
  now: number;
}): IdlePhase {
  const timeout = idleTimeoutMs(args.setting);
  const idleAt = timeout !== null && args.rotatedAt !== null ? args.rotatedAt + timeout : null;
  const absoluteAt = absoluteDeadline(args.sessionExpiresAt);

  // La première des deux l'emporte. À égalité, l'inactivité : elle se repousse, l'autre non — et
  // proposer le geste qui marche vaut mieux que celui qui ne peut rien.
  let deadline: number;
  let limit: SessionLimit;
  if (idleAt !== null && (absoluteAt === null || idleAt <= absoluteAt)) {
    deadline = idleAt;
    limit = 'idle';
  } else if (absoluteAt !== null) {
    deadline = absoluteAt;
    limit = 'absolute';
  } else {
    return { kind: 'safe' };
  }

  const msLeft = deadline - args.now;
  if (msLeft <= 0) return { kind: 'expired', limit };

  // Le préavis de l'inactivité s'ajuste à son délai ; la fin absolue n'en a pas à quoi s'ajuster.
  const warnBefore = limit === 'idle' ? warnBeforeMs(args.setting) : MAX_WARN_BEFORE_MS;
  if (msLeft <= warnBefore) return { kind: 'warn', msLeft, limit, deadline };
  return { kind: 'safe' };
}

/** `mm:ss`, pour un décompte qu'on lit d'un coup d'œil. */
export function formatCountdown(msLeft: number): string {
  const totalSeconds = Math.max(0, Math.ceil(msLeft / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Faut-il renouveler maintenant ?
 *
 * Sortie du composant pour être vérifiable : c'est la règle qui décide combien de requêtes part
 * vers une route que le serveur utilise aussi pour servir les navigations. Une erreur ici ne se
 * voit pas — elle se sent, sous forme d'application qui traîne.
 *
 * Trois conditions, et la première est celle qui manquait (#1241) :
 *
 * - **une tentative espace les suivantes**, réussie ou non. Compter sur la dernière *réussite*
 *   laissait un échec relancer un essai à chaque battement, soit une requête par seconde ;
 * - la toute première a lieu au chargement, faute d'ancre : c'est elle qui rend l'échéance
 *   connaissable, et sans elle aucun avertissement n'est possible ;
 * - ensuite, il faut un geste depuis le dernier renouvellement réussi — sans quoi on entretiendrait
 *   une session que personne n'utilise, ce que le réglage demande justement de couper.
 */
export function shouldRotate(args: {
  lastAttemptAt: number | null;
  rotatedAt: number | null;
  lastActivityAt: number;
  interval: number;
  now: number;
}): boolean {
  if (args.lastAttemptAt !== null && args.now - args.lastAttemptAt < args.interval) return false;
  if (args.rotatedAt === null) return true;
  return args.lastActivityAt > args.rotatedAt;
}
